import React from 'react';
import { LayoutDashboard, Receipt, Wallet, CreditCard, Eye, Landmark, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * Mini-preview ao vivo de como o sistema do cliente ficará.
 * O visual segue o padrão Nexus (iguais para todos os clientes); o que muda
 * em tempo real é só o logo e os nomes.
 */

const ACCENT = '#6366F1';

export default function BrandingPreview({
  nomeDoSistema = 'Gastify',
  nomeEmpresa = 'Sua empresa',
  logo = '',
}) {
  const navItems = [
    { label: 'Dashboard', icon: LayoutDashboard, active: true },
    { label: 'Lançamentos', icon: Receipt },
    { label: 'Carteira', icon: Wallet },
    { label: 'Cartão de Crédito', icon: CreditCard },
  ];

  const cards = [
    { label: 'Saldo atual', icon: Landmark, color: '#6366F1' },
    { label: 'Receitas', icon: ArrowUp, color: '#10B981' },
    { label: 'Despesas', icon: ArrowDown, color: '#EF4444' },
  ];

  return (
    <div className="space-y-3" data-testid="branding-preview">
      <div className="flex items-center gap-2">
        <Eye size={14} className="text-slate-400" />
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Preview ao vivo</p>
      </div>

      <div className="bg-slate-100 rounded-2xl border border-slate-200 overflow-hidden shadow-inner">
        <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-200/60 border-b border-slate-200">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-2 text-[10px] text-slate-500 font-mono truncate">/{(nomeEmpresa || 'tenant').toLowerCase().replace(/\s+/g, '-')}/dashboard</span>
        </div>

        <div className="flex p-2 gap-2" style={{ minHeight: '280px', background: '#EAEFF5' }}>
          {/* Sidebar (vidro claro, igual ao sistema) */}
          <div
            className="w-[116px] flex-shrink-0 flex flex-col rounded-2xl border"
            style={{
              background: 'rgba(255,255,255,0.8)',
              borderColor: 'rgba(255,255,255,0.9)',
              boxShadow: '0 10px 24px -8px rgba(112,144,176,0.25)',
              color: '#0f172a',
            }}
          >
            <div className="px-2.5 py-2.5 border-b" style={{ borderColor: 'rgba(15,23,42,0.06)' }}>
              <div className="flex items-start gap-1.5">
                {logo ? (
                  <img src={logo} alt="" className="w-6 h-6 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" style={{ background: ACCENT }}>
                    {(nomeDoSistema || 'G')[0].toUpperCase()}
                  </div>
                )}
                <p className="text-[8.5px] font-extrabold uppercase leading-tight break-words tracking-wide" style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {nomeDoSistema || 'Gastify'}
                </p>
              </div>
              <p className="mt-1 text-[7px] font-bold uppercase tracking-widest truncate" style={{ color: '#059669' }}>
                {nomeEmpresa || 'Sua empresa'}
              </p>
            </div>
            <div className="flex-1 px-1.5 py-2 space-y-1">
              {navItems.map(it => (
                <div key={it.label}
                  className="flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg text-[8.5px] font-semibold"
                  style={{
                    background: it.active ? '#fff' : 'transparent',
                    boxShadow: it.active ? '0 4px 10px -3px rgba(112,144,176,0.3)' : 'none',
                    color: it.active ? '#0f172a' : '#64748b',
                  }}>
                  <it.icon size={10} className="flex-shrink-0" />
                  <span className="truncate">{it.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-[9px] font-bold text-slate-700">Dashboard</p>
            <div className="grid grid-cols-1 gap-1.5">
              {cards.map(c => (
                <div key={c.label} className="bg-white rounded-xl border border-white shadow-sm px-2 py-1.5 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: c.color }}>
                    <c.icon size={10} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[7px] font-semibold text-slate-400">{c.label}</p>
                    <p className="text-[10px] font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>R$ 0,00</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full text-white text-[8px] font-bold py-1.5 rounded-lg text-center" style={{ background: ACCENT }}>
              + Novo Lançamento
            </div>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 leading-snug">
        Todos os clientes usam o mesmo visual padrão. Aqui você personaliza apenas o nome e o logo.
      </p>
    </div>
  );
}
